// =============================================================================
// VandieZ admin-web -- Dashboard (/)
// =============================================================================
// Landing page after login. Now a slim KPI overview rendered inside the
// shared AdminLayout shell; the per-surface navigation lives in the
// AdminLayout sidebar, so this page just surfaces the few numbers an ops
// lead wants to see at a glance:
//   * Signups (last 24h)
//   * KYC queue depth
//   * Live trips
//   * Open support tickets
//
// Each tile fetches its own count via the existing admin API endpoints;
// failures fall back to "—" without breaking the page.
// =============================================================================

import {
  Activity,
  ClipboardCheck,
  LifeBuoy,
  Scale,
  Shield,
  Siren,
  Truck,
  Users,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  atRiskAdminApi,
  disputesAdminApi,
  fraudAdminApi,
  kycAdminApi,
  opsAdminApi,
  supportAdminApi,
  systemAdminApi,
  tripsAdminApi,
  vehicleAdminApi,
} from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

export function DashboardPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [kycDepth, setKycDepth] = useState<number | null>(null);
  const [liveTrips, setLiveTrips] = useState<number | null>(null);
  const [openTickets, setOpenTickets] = useState<number | null>(null);
  const [openDisputes, setOpenDisputes] = useState<number | null>(null);
  const [vehicleQueue, setVehicleQueue] = useState<number | null>(null);
  const [signups24h, setSignups24h] = useState<number | null>(null);
  const [atRiskTrips, setAtRiskTrips] = useState<number | null>(null);
  const [fraudUsers, setFraudUsers] = useState<number | null>(null);
  const [systemOk, setSystemOk] = useState<boolean | null>(null);

  useEffect(() => {
    if (accessToken === null) {return;}
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await kycAdminApi.queue(
          accessToken,
          { limit: 1, offset: 0 },
          controller.signal,
        );
        setKycDepth(res.total);
      } catch {
        // Tile shows "—" on failure; /kyc/queue surfaces the real error.
      }
    })();
    void (async () => {
      try {
        const res = await tripsAdminApi.listLive(
          accessToken,
          { limit: 1, offset: 0 },
          controller.signal,
        );
        setLiveTrips(res.total);
      } catch {
        // Tile shows "—" on failure; /trips/live surfaces the real error.
      }
    })();
    void (async () => {
      try {
        const res = await supportAdminApi.queue(
          accessToken,
          { status: "open", limit: 1, offset: 0 },
          controller.signal,
        );
        setOpenTickets(res.total);
      } catch {
        // Tile shows "—" on failure; /tickets surfaces the real error.
      }
    })();
    void (async () => {
      try {
        const res = await opsAdminApi.overview(
          accessToken,
          controller.signal,
        );
        setSignups24h(res.signups.last_24h);
      } catch {
        // Tile shows "—" on failure; /ops surfaces the real error.
      }
    })();
    void (async () => {
      try {
        const res = await disputesAdminApi.queue(
          accessToken,
          { status: "open", limit: 1, offset: 0 },
          controller.signal,
        );
        setOpenDisputes(res.total);
      } catch {
        // Tile shows "—" on failure.
      }
    })();
    void (async () => {
      try {
        const res = await vehicleAdminApi.queue(
          accessToken,
          { limit: 1, offset: 0 },
          controller.signal,
        );
        setVehicleQueue(res.total);
      } catch {
        // Tile shows "—" on failure.
      }
    })();
    void (async () => {
      try {
        const res = await atRiskAdminApi.list(
          accessToken,
          { limit: 500 },
          controller.signal,
        );
        setAtRiskTrips(res.rows.length);
      } catch {
        // Tile shows "—" on failure; /at-risk surfaces the real error.
      }
    })();
    void (async () => {
      try {
        const res = await fraudAdminApi.signals(
          accessToken,
          { days: 7, limit: 500 },
          controller.signal,
        );
        setFraudUsers(res.rows.length);
      } catch {
        // Tile shows "—" on failure.
      }
    })();
    void (async () => {
      try {
        const res = await systemAdminApi.health(
          accessToken,
          controller.signal,
        );
        setSystemOk(
          res.redis.reachable &&
            res.db.ping_ms < 500 &&
            res.traffic.errors_last_1h < 50,
        );
      } catch {
        // Tile shows "—" on failure.
      }
    })();
    return () => { controller.abort(); };
  }, [accessToken]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Daily ops snapshot. Use the sidebar to drill into any surface."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-9">
        <KpiTile
          label="Signups today"
          value={signups24h}
          subtitle="New users in the last 24h."
          icon={Users}
          to="/ops"
        />
        <KpiTile
          label="KYC queue"
          value={kycDepth}
          subtitle="Users awaiting review."
          icon={ClipboardCheck}
          to="/kyc/queue"
          urgent={kycDepth !== null && kycDepth > 0}
        />
        <KpiTile
          label="Vehicle queue"
          value={vehicleQueue}
          subtitle="Vehicles pending approval."
          icon={Truck}
          to="/vehicles/queue"
          urgent={vehicleQueue !== null && vehicleQueue > 0}
        />
        <KpiTile
          label="Live trips"
          value={liveTrips}
          subtitle="Trips currently in flight."
          icon={Truck}
          to="/trips/live"
        />
        <KpiTile
          label="Open disputes"
          value={openDisputes}
          subtitle="Disputes awaiting decision."
          icon={Scale}
          to="/disputes"
          urgent={openDisputes !== null && openDisputes > 0}
        />
        <KpiTile
          label="Open tickets"
          value={openTickets}
          subtitle="Tickets awaiting reply."
          icon={LifeBuoy}
          to="/tickets"
          urgent={openTickets !== null && openTickets > 0}
        />
        <KpiTile
          label="Trips at risk"
          value={atRiskTrips}
          subtitle="Stuck / overdue / payment stuck."
          icon={Siren}
          to="/at-risk"
          urgent={atRiskTrips !== null && atRiskTrips > 0}
        />
        <KpiTile
          label="Fraud signals"
          value={fraudUsers}
          subtitle="Flagged users in last 7d."
          icon={Shield}
          to="/fraud"
          urgent={fraudUsers !== null && fraudUsers > 0}
        />
        <KpiTile
          label="System health"
          value={systemOk === null ? null : systemOk ? 1 : 0}
          displayValue={
            systemOk === null ? "—" : systemOk ? "OK" : "DEGRADED"
          }
          subtitle="DB + Redis + error rate."
          icon={Activity}
          to="/system-health"
          urgent={systemOk === false}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Shortcuts</CardTitle>
          <CardDescription>
            Every admin surface lives in the sidebar to your left — KYC
            review, vehicle approvals, live trips, payouts, commission
            rules, disputes, tickets, support mediation, SMS settings,
            users, and roles. Open the Ops dashboard for the full money +
            people KPI breakdown.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

type IconComponent = typeof Users;

function KpiTile({
  label,
  value,
  displayValue,
  subtitle,
  icon: Icon,
  to,
  urgent = false,
}: {
  label: string;
  value: number | null;
  displayValue?: string;
  subtitle: string;
  icon: IconComponent;
  to: string;
  urgent?: boolean;
}): React.JSX.Element {
  return (
    <Link to={to} className="block">
      <Card
        className={cn(
          "transition-colors hover:border-primary hover:shadow-sm",
          urgent && "border-warning",
        )}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardDescription>{label}</CardDescription>
            <Icon
              className={cn(
                "h-4 w-4",
                urgent ? "text-warning-subtle-foreground" : "text-muted-foreground",
              )}
            />
          </div>
          <CardTitle
            className={cn(
              "text-2xl tabular-nums",
              urgent && "text-warning-subtle-foreground",
            )}
          >
            {displayValue ?? value ?? "—"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          {subtitle}
        </CardContent>
      </Card>
    </Link>
  );
}
